/**
 * German language strings for the Vivatis PDB application.
 * All user-facing text should be defined here for easy localisation.
 */

const de = {
  /* ------------------------------------------------------------------ */
  /* Global / shared                                                    */
  /* ------------------------------------------------------------------ */
  common: {
    products: 'Produkte',
    product: 'Produkt',
    productsPlural: 'Produkte',
    partner: 'Partner',
    name: 'Name',
    category: 'Kategorie',
    yes: 'Ja',
    no: 'Nein',
    back: 'Zurück',
    next: 'Weiter',
    skip: 'Überspringen',
    filter: 'Filter',
    filterMode: 'Filtermodus',
    and: 'UND',
    or: 'ODER',
    clearAll: 'Alle löschen',
    removeAll: 'Alle entfernen',
    browseProducts: 'Produkte durchsuchen',
    noProductsFound: 'Keine Produkte gefunden.',
    noResultsFor: (term: string) => `Keine Ergebnisse für „${term}“.`,
    new: 'NEU',
    pdf: 'PDF',
    viewAll: 'Alle anzeigen',
    offlineBanner: 'Du bist offline. Einige Funktionen sind eingeschränkt.',
    offlineTooltip: 'Nicht verfügbar im Offline-Modus',
    offlineLanguageHint: 'Offline: Die Oberflächen-Sprache wird gewechselt, die Produktdaten bleiben in der zuvor geladenen Sprache, bis du wieder online bist',
    pwaUpdateAvailable: 'Eine neue Version ist verfügbar. Wird aktualisiert...',
    pwaInstallTitle: 'App installieren',
    pwaInstallDescription: 'Zum Startbildschirm hinzufügen für schnellen Zugriff.',
    pwaInstallButton: 'Installieren',
  },

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */
  nav: {
    home: 'Startseite',
    products: 'Produkte',
    wizard: 'Assistent',
    favorites: 'Favoriten',
    compare: 'Vergleichen',
    cart: 'Dokumente',
    searchPlaceholder: 'Produkte suchen...',
    toggleTheme: 'Design wechseln',
    logout: 'Abmelden',
    admin: 'Admin',
    toggleMenu: 'Menü umschalten',
  },

  /* ------------------------------------------------------------------ */
  /* Branding                                                           */
  /* ------------------------------------------------------------------ */
  brand: {
    vivatis: 'VIVATIS',
    pdb: 'PDB',
    pharma: 'PHARMA',
    productDatabase: 'Produktdatenbank',
    copyright: 'Vivatis Pharma GmbH © 2026',
  },

  /* ------------------------------------------------------------------ */
  /* Login                                                              */
  /* ------------------------------------------------------------------ */
  login: {
    email: 'E-Mail',
    password: 'Passwort',
    showPassword: 'Passwort anzeigen',
    hidePassword: 'Passwort verbergen',
    signingIn: 'Anmeldung...',
    signIn: 'Anmelden',
    forgotPassword: 'Passwort vergessen?',
  },

  /* ------------------------------------------------------------------ */
  /* Home page                                                          */
  /* ------------------------------------------------------------------ */
  home: {
    welcomeBack: (name: string) => `Willkommen zurück${name ? `, ${name}` : ''}!`,
    whatToDo: 'Was möchten Sie heute tun?',
    stats: {
      products: 'Produkte',
      new: 'Neu',
      favorites: 'Favoriten',
      inCart: 'Im Dokument',
    },
    actions: {
      browseProducts: 'Produkte durchsuchen',
      viewAllProducts: (count: number) => (count === 1 ? 'Ein Produkt anzeigen' : `Alle ${count} Produkte anzeigen`),
      browseProductsCount: (count: number) => `${count} Produkte`,
      loading: 'Laden...',
      startWizard: 'Assistent starten',
      findMatching: 'Passende Produkte finden',
      favorites: 'Favoriten',
      productsSaved: (count: number) => `${count} gespeichert`,
      meetingCart: 'Besprechungsdokument',
      productsInCart: (count: number) => `${count} im Dokument`,
    },
    browseByIndication: 'Nach Indikation durchsuchen',
    topIndications: 'Ihre Top-Indikationen',
    topIndicationsEmpty: 'Beginnen Sie mit dem Durchsuchen von Produkten, um Ihre meistbesuchten Indikationen hier zu sehen.',
    newProducts: 'Neue Produkte',
    newProductsTooltip: 'Alle Produkte, die in den letzten 12 Monaten hinzugefügt wurden, gelten als neu.',
    newStudies: 'Neue Studien',
    newStudiesTooltip: 'Produkte mit neu veröffentlichten klinischen Studien in den letzten 12 Monaten.',
    newStudyBadge: 'Neue Studie',
    publishedStudies: (count: number) => `${count} veröffentlichte klinische Studien`,
    favoritesTitle: 'Favoriten',
    noFavorites: 'Noch keine Favoriten. Klicken Sie auf das Herz-Symbol eines Produkts, um es hier zu speichern.',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'Produkt' : 'Produkte'}`,
    recentlyViewed: 'Zuletzt angesehen',
    noRecentlyViewed: 'Noch keine Produkte angesehen. Beginnen Sie mit dem',
    productCatalog: 'Produktkatalog',
    viewAll: 'Alle anzeigen',
  },

  /* ------------------------------------------------------------------ */
  /* Product list                                                       */
  /* ------------------------------------------------------------------ */
  productList: {
    sort: {
      relevance: 'Relevanz',
      nameAsc: 'Name (A-Z)',
      nameDesc: 'Name (Z-A)',
      articleNumber: 'Artikelnr.',
      partner: 'Partner',
      indication: 'Indikation',
    },
    viewTable: 'Tabelle',
    viewCards: 'Karten',
    productsFound: (count: number) => `${count} Produkte gefunden`,
    productCount: (count: number, total: number) => `${count} von ${total} Produkten`,
    loading: 'Produkte werden geladen...',
    adjustFilters: 'Passen Sie Ihre Filter an.',
    resetFilters: 'Filter zurücksetzen',
  },

  /* ------------------------------------------------------------------ */
  /* Product search                                                     */
  /* ------------------------------------------------------------------ */
  productSearch: {
    placeholder: 'Nach Name, Artikelnummer, Inhaltsstoff, Indikation suchen...',
  },

  /* ------------------------------------------------------------------ */
  /* Product table                                                      */
  /* ------------------------------------------------------------------ */
  productTable: {
    articleNo: 'Artikelnr.',
    name: 'Name',
    partner: 'Partner',
    indication: 'Indikation',
    application: 'Anwendung',
    country: 'Land',
    dailyDose: 'Tagesdosis',
    claims: 'Claims',
    actions: 'Aktionen',
  },

  /* ------------------------------------------------------------------ */
  /* Product detail                                                     */
  /* ------------------------------------------------------------------ */
  productDetail: {
    tabs: {
      overview: 'Übersicht',
      productData: 'Produktdaten',
      moreDetails: 'Preis',
    },
    notFound: 'Produkt nicht gefunden',
    backToOverview: 'Zurück zur Übersicht',
    marketingClaims: 'Marketing Claims',
    indications: 'Indikationen',
    applications: 'Anwendungen',
    countries: 'Länder',
    description: 'Beschreibung',
    shortDescription: 'Kurzbeschreibung',
    website: 'Produkt-Webseite',
    multimedia: 'Multimedia-Webseite',
    studies: 'Studien',
    origin: 'Herkunft',
    dosage: 'Dosierung',
    appearance: 'Erscheinungsbild',
    category: 'Kategorie',
    articleNumber: 'Artikelnummer',
    active: 'Aktiv',
    created: 'Erstellt',
    showDailyPrice: 'Tagespreis anzeigen',
    perDay: '/ Tag',
    generatingPdf: 'PDF wird erstellt...',
    sendViaEmail: 'Per E-Mail senden',
    emailSubject: (name: string, companyName: string) => `${companyName} — Produktdatenblatt: ${name}`,
    emailBody: (name: string, userName: string, companyName: string) =>
      `Sehr geehrter Kunde,\n\nanbei erhalten Sie das Produktdatenblatt für ${name}.\n\nBitte lassen Sie mich wissen, wenn Sie Fragen haben oder weitere Informationen benötigen.\n\nMit freundlichen Grüßen,\n${userName}\n${companyName}`,
    toastEmail: 'E-Mail erfolgreich gesendet!',
  },

  /* ------------------------------------------------------------------ */
  /* Actions (favorite, cart, compare)                                   */
  /* ------------------------------------------------------------------ */
  actions: {
    addToFavorites: 'Zu Favoriten hinzufügen',
    removeFromFavorites: 'Aus Favoriten entfernen',
    favorited: 'Als Favorit markiert',
    favorite: 'Favorit',
    addToCart: 'Zum Dokument hinzufügen',
    removeFromCart: 'Aus Dokument entfernen',
    removeFromAllCarts: 'Aus allen Dokumenten entfernen',
    inCart: 'Im Dokument',
    addToCartLabel: 'Zum Dokument hinzufügen',
    inCartLabel: 'Im Dokument',
    cartLabel: 'Dokumente',
    addToComparison: 'Zum Vergleich hinzufügen',
    removeFromComparison: 'Aus Vergleich entfernen',
    inComparison: 'Im Vergleich',
    compared: 'Verglichen',
    compare: 'Vergleichen',
    manageFavorites: 'Favoriten verwalten',
    addToFavoritesNoGroup: 'Zu Favoriten (ohne Gruppe)',
    favoritesLabel: 'Favoriten',
  },

  /* ------------------------------------------------------------------ */
  /* Favorites page                                                     */
  /* ------------------------------------------------------------------ */
  favorites: {
    title: 'Favoriten',
    noFavorites: 'Keine Favoriten',
    noFavoritesDescription: 'Markieren Sie Produkte als Favoriten, um sie hier zu sehen.',
    deleteGroup: {
      title: 'Gruppe löschen',
      message: (name: string) => `Möchten Sie die Gruppe „${name}" wirklich löschen?`,
      moveLabel: 'Produkte verschieben nach:',
      ungroupedOption: 'Nicht in einer Gruppe (ungruppiert)',
      confirm: 'Gruppe löschen',
      cancel: 'Abbrechen',
    },
    browseProducts: 'Produkte durchsuchen',
    dragProductsHint: 'Produkte hierher ziehen',
    notInGroup: 'Nicht in einer Gruppe',
    notInGroupWithCount: (count: number) => `Nicht in einer Gruppe (${count})`,
    dragCopyHint: '⌘/⇧ gehalten — loslassen zum Kopieren in Gruppe',
    dragMoveHint: 'Loslassen zum Verschieben · ⌘/⇧ halten zum Kopieren',
    addGroupToDocument: 'Gruppe zum Dokument hinzufügen',
    deleteGroupTitle: 'Gruppe löschen',
    groupNamePlaceholder: 'Gruppenname...',
    create: 'Erstellen',
    cancel: 'Abbrechen',
    newGroup: 'Neue Gruppe',
    inDocument: 'Im Dokument',
    alreadyInDocument: 'Die Produkte sind bereits im Dokument.',
    addedToDocument: 'Produkte zum Dokument hinzugefügt.',
    removedFromDocument: 'Produkte aus dem Dokument entfernt.',
    addToDocument: 'Hinzufügen',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'Produkt' : 'Produkte'}`,
    openProductTitle: 'Öffnen',
  },

  /* ------------------------------------------------------------------ */
  /* Comparison page                                                    */
  /* ------------------------------------------------------------------ */
  comparison: {
    title: 'Produktvergleich',
    loading: 'Produkte werden geladen...',
    productCount: (count: number, max: number) => `(${count}/${max})`,
    noProducts: 'Keine Produkte zum Vergleichen',
    noProductsDescription: 'Fügen Sie Produkte hinzu, um sie nebeneinander zu vergleichen.',
    browseProducts: 'Produkte durchsuchen',
    addProduct: 'Produkt hinzufügen',
    removeFromComparison: 'Aus dem Vergleich entfernen',
    removeAll: 'Alle entfernen',
    showDifferencesOnly: 'Nur Unterschiede anzeigen',
    showingDifferencesOnly: 'Nur Unterschiede werden angezeigt',
    hideDailyPrice: 'Tagespreis ausblenden',
    dailyPriceHidden: 'Tagespreis ausgeblendet',
    hidePrice: 'Preis ausblenden',
    priceHidden: 'Preis ausgeblendet',
    allIdentical: 'Alle Werte sind bei diesen Produkten identisch.',
    rows: {
      articleName: 'Artikelname',
      articleNumber: 'Artikelnummer',
      partner: 'Partner',
      manufacturer: 'Hersteller',
      category: 'Kategorie',
      clinicalStudies: 'Klinische Studien',
      bioactiveIngredients: 'Wirkstoffe / Assay',
      indications: 'Indikationen',
      applications: 'Anwendungen',
      countries: 'Länder',
      dosage: 'Dosierung',
      dailyDosage: 'Tagesdosis',
      appearance: 'Erscheinungsbild',
      origin: 'Herkunft',
      sourceOrigin: 'Quelle / Herkunft',
      marketingClaims: 'Marketing Claims',
      dailyPrice: 'Tagespreis',
      price: 'Preis',
      novelFood: 'Novel Food',
    },
    novelFoodStatuses: {
      yes: 'Ja',
      ongoing: 'Laufend',
      approved: 'Zugelassen',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Cart page                                                          */
  /* ------------------------------------------------------------------ */
  cart: {
    title: 'Besprechungsdokument',
    subtitle: 'Produkte für Ihr Kundengespräch',
    emptyTitle: 'Ihr Dokument ist leer',
    emptyDescription: 'Fügen Sie Produkte aus dem Katalog hinzu.',
    moveUp: 'Nach oben',
    moveDown: 'Nach unten',
    remove: 'Entfernen',
    rename: 'Umbenennen',
    lockedNotice: (date: string) => `Dieses Dokument wurde am ${date} versendet und kann nicht mehr geändert werden.`,
    generatePdf: 'PDF erstellen',
    generatingPdf: 'PDF wird erstellt...',
    sendViaEmail: 'Per E-Mail senden',
    createOpportunity: 'Opportunity in SalesForce anlegen',
    clearCart: 'Dokument leeren',
    archiveDocument: 'Dokument archivieren',
    confirmArchive: (name: string) => `„${name}" archivieren? Es wird nicht mehr in Ihrer Dokumentenliste angezeigt und ist danach nur noch für Administratoren einsehbar.`,
    deleteDocument: 'Dokument löschen',
    confirmClear: 'Möchten Sie das Dokument wirklich leeren?',
    confirmDelete: (name: string) => `„${name}" löschen? Das Dokument und alle enthaltenen Produkte werden entfernt.`,
    toastPdf: 'PDF wurde erstellt und heruntergeladen.',
    toastPdfError: 'PDF-Erstellung fehlgeschlagen.',
    toastEmail: 'E-Mail erfolgreich gesendet!',
    showDocuments: 'Dokumente anzeigen',
    newDocument: 'Neues Dokument',
    searchDocuments: 'Dokumente suchen...',
    noDocumentsFound: 'Keine Dokumente gefunden.',
    items: 'Einträge',
    comingSoon: 'Demnächst verfügbar',
    comingSoonDescription: 'Die SalesForce-Integration befindet sich derzeit in Entwicklung. Diese Funktion wird in einem zukünftigen Update verfügbar sein.',
    productCount: (count: number) => `${count} ${count === 1 ? 'Produkt' : 'Produkte'}`,
    emailSubject: (count: number, companyName: string) => `${companyName} — Produktauswahl (${count} Produkte)`,
    emailBody: (productList: string, userName: string, companyName: string) => `Sehr geehrter Kunde,\n\nnachfolgend finden Sie die Produktauswahl aus unserem letzten Gespräch:\n\n${productList}\n\nBitte lassen Sie mich wissen, wenn Sie Fragen haben oder weitere Informationen zu einem der Produkte benötigen.\n\nMit freundlichen Grüßen,\n${userName}\n${companyName}`,
    email: {
      title: 'Per E-Mail senden',
      recipients: 'Empfänger',
      subject: 'Betreff',
      message: 'Nachricht',
      placeholder: 'E-Mail-Adresse eingeben...',
      cancel: 'Abbrechen',
      send: 'E-Mail senden',
      sending: 'Wird gesendet...',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      duplicateEmail: 'Diese E-Mail-Adresse wurde bereits hinzugefügt.',
      sendError: 'E-Mail konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
      products: 'Produkte',
      productsCount: (selected: number, total: number) => `${selected} von ${total}`,
      selectAll: 'Alle auswählen',
      deselectAll: 'Alle abwählen',
      noProductsWarning: 'Wählen Sie mindestens ein Produkt für die E-Mail aus.',
      fileTypesLegend: 'Dokumente beilegen',
      fileTypesLoading: 'Verfügbare Dokumente werden geladen …',
      fileTypes: {
        study: 'Studien',
        marketing: 'Marketing-Dokumente',
        quality: 'Qualitätsdokumente',
      },
      qualityHint: 'Die Qualitätsdokumente werden nicht angehängt, sondern separat von {email} an die Empfänger verschickt.',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Settings                                                           */
  /* ------------------------------------------------------------------ */
  settings: {
    title: 'Einstellungen',
    subtitle: 'Konfigurieren Sie Ihre Anwendungseinstellungen',
    multiCart: {
      title: 'Mehrere Dokumente',
      description:
        'Aktivieren Sie mehrere Besprechungsdokumente, um Produkte für verschiedene Kunden oder Besprechungen zu organisieren. Sie können bis zu 3 Dokumente erstellen und über die Kopfzeile zwischen ihnen wechseln.',
    },
    favoritesOnHome: {
      title: 'Favoriten auf der Startseite',
      description: 'Zeigen Sie Ihren Favoriten-Bereich auf der Startseite für schnellen Zugriff auf gespeicherte Produkte.',
    },
    favoriteGroups: {
      title: 'Favoriten-Gruppen',
      description: 'Organisieren Sie Ihre Favoriten in Gruppen. Sie können bis zu 5 Gruppen erstellen und Produkte zwischen ihnen verschieben. Jede Gruppe kann mit einem Klick zu einem Dokument hinzugefügt werden.',
    },
    productData: {
      title: 'Produktdaten',
      description: 'Zeigt an, wann die Produktdaten zuletzt vom Server geladen wurden.',
      lastLoaded: 'Zuletzt geladen',
      never: 'Noch nie geladen',
      reload: 'Produkte neu laden',
      reloading: 'Wird neu geladen...',
      reloadSuccess: 'Produktdaten neu geladen',
      reloadError: 'Produktdaten konnten nicht neu geladen werden',
    },
    changePassword: {
      title: 'Passwort ändern',
      description: 'Aktualisieren Sie das Passwort, mit dem Sie sich anmelden.',
      currentPassword: 'Aktuelles Passwort',
      newPassword: 'Neues Passwort',
      confirmPassword: 'Neues Passwort bestätigen',
      submit: 'Passwort aktualisieren',
      submitting: 'Wird aktualisiert...',
      success: 'Ihr Passwort wurde aktualisiert.',
      passwordMismatch: 'Passwörter stimmen nicht überein',
      error: 'Passwort konnte nicht aktualisiert werden',
      backToSettings: 'Zurück zu den Einstellungen',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Wizard                                                             */
  /* ------------------------------------------------------------------ */
  wizard: {
    steps: {
      country: 'Land',
      application: 'Anwendung',
      indication: 'Indikation',
    },
    selectCountry: 'Land auswählen',
    selectCountrySubtitle: 'In welchen Ländern soll das Produkt verfügbar sein?',
    selectApplication: 'Anwendung auswählen',
    selectApplicationSubtitle: 'Welche Darreichungsform bevorzugen Sie?',
    selectIndication: 'Indikation auswählen',
    selectIndicationSubtitle: 'Welche Gesundheitsbereiche sind relevant?',
    showResults: 'Ergebnisse anzeigen',
    noSelection: 'Keine Auswahl getroffen',
    selected: (count: number) => `${count} ausgewählt`,
    filterAnd: 'Und',
    filterOr: 'Oder',
    filterAndExplanation: 'Produkte müssen alle ausgewählten Kriterien erfüllen',
    filterOrExplanation: 'Produkte müssen mindestens ein Kriterium pro Schritt erfüllen',
  },

  /* ------------------------------------------------------------------ */
  /* Filter sidebar                                                     */
  /* ------------------------------------------------------------------ */
  filterSidebar: {
    title: 'Filter',
    category: 'Kategorie',
    country: 'Land',
    application: 'Anwendung',
    indication: 'Indikation',
    partner: 'Partner',
    marketingClaims: 'Marketing Claims',
    agreement: 'Vereinbarung',
    agreements: {
      DA: 'DA — Distributionsvertrag',
      CPA: 'CPA — Kundenschutzvereinbarung',
      Gentleman: 'Gentleman Agreement',
      LOI: 'LOI — Absichtserklärung',
      none: 'Keine Vereinbarung',
    },
    agreementDescriptions: {
      DA: 'Distributionsvertrag (exklusiv)',
      CPA: 'Kundenschutzvereinbarung (nicht-exklusiv)',
      Gentleman: 'Gentleman Agreement (mündlich, ohne Vertrag)',
      LOI: 'Absichtserklärung (Vorstufe zum DA)',
      none: 'Noch keine Vereinbarung für dieses Land',
    },
    newProducts: 'Neue Produkte',
    newStudy: 'Neue Studie',
    restrictedTo: (territories: string) => `Deine Ansicht ist auf ${territories} beschränkt.`,
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* Not Found (404)                                                    */
  /* ------------------------------------------------------------------ */
  notFound: {
    title: '404',
    heading: 'Seite nicht gefunden',
    description: 'Die gesuchte Seite existiert nicht oder wurde verschoben.',
    goBack: 'Zurück',
    home: 'Startseite',
  },

  /* ------------------------------------------------------------------ */
  /* Forgot Password                                                    */
  /* ------------------------------------------------------------------ */
  forgotPassword: {
    subtitle: 'Passwort zurücksetzen',
    email: 'E-Mail',
    emailPlaceholder: 'ihre@email.com',
    submit: 'Reset-Link senden',
    submitting: 'Wird gesendet...',
    success: 'Wir haben einen Link zum Zurücksetzen des Passworts an Ihre E-Mail-Adresse gesendet.',
    backToLogin: 'Zurück zur Anmeldung',
    error: 'Reset-Link konnte nicht gesendet werden',
  },

  /* ------------------------------------------------------------------ */
  /* Reset Password                                                     */
  /* ------------------------------------------------------------------ */
  resetPassword: {
    subtitle: 'Neues Passwort festlegen',
    email: 'E-Mail',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort bestätigen',
    submit: 'Passwort zurücksetzen',
    submitting: 'Wird zurückgesetzt...',
    passwordMismatch: 'Passwörter stimmen nicht überein',
    backToLogin: 'Zurück zur Anmeldung',
    error: 'Passwort konnte nicht zurückgesetzt werden',
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  claimDescriptions: {
    Vegan: 'Enthält keine tierischen Zutaten oder Nebenprodukte.',
    Organic: 'Zertifiziert biologisch — angebaut ohne synthetische Pestizide oder Düngemittel.',
    'Non-GMO': 'Frei von gentechnisch veränderten Organismen.',
    'Clinically Studied': 'Wirksamkeit durch veröffentlichte klinische Studien belegt.',
    Patented: 'Durch ein oder mehrere Patente für einzigartige Formulierung oder Verfahren geschützt.',
    Halal: 'Halal-zertifiziert — konform mit islamischen Ernährungsrichtlinien.',
    Kosher: 'Koscher-zertifiziert — konform mit jüdischen Ernährungsgesetzen.',
    'Gluten-Free': 'Enthält kein Gluten — geeignet für Menschen mit Zöliakie oder Glutensensitivität.',
  },
  pdf: {
    productDataSheet: 'Produktdatenblatt',
    productDataSheets: 'Produktdatenblaetter',
    keyFacts: 'Eckdaten',
    dailyDosage: 'Tagesdosis',
    appearance: 'Aussehen',
    bioactiveIngredient: 'Bioaktiver Inhaltsstoff',
    indications: 'Indikationen',
    possibleApplications: 'Anwendungsformen',
    description: 'Beschreibung',
    linkStudies: 'Studien',
    origin: 'Herkunft',
    confidential: 'Vertraulich -- Nur fuer geschaeftliche Zwecke',
    productCountOne: ':count Produkt',
    productCountOther: ':count Produkte',
    clinicalStudies: 'Klinische Studien',
    marketingClaims: 'Marketing Claims',
    territories: 'Verkaufsgebiete',
    moq: 'Mindestbestellmenge in kg',
    shelfLife: 'Haltbarkeit',
    shelfLifeValue: ':months Monate',
    novelFoodStatus: 'Novel Food',
    badgeNew: 'NEU',
    badgeNewStudy: 'NEUE STUDIE',
    contactPerson: 'Ihr Ansprechpartner bei Vivatis:',
    website: 'Produkt-Webseite',
    multimedia: 'Multimedia-Webseite',
  },
} as const;

export default de;
